import React, { useMemo } from 'react';
import { makeStyles } from '@material-ui/core/styles';
import { DragDropContext, Droppable } from 'react-beautiful-dnd';
import KanbanColumn from './KanbanColumn';

const useStyles = makeStyles(theme => ({
  board: {
    display: 'flex',
    alignItems: 'flex-start',
    gap: 12,
    flex: 1,
    minHeight: 0,
    overflowX: 'auto',
    overflowY: 'hidden',
    scrollBehavior: 'smooth',
    overscrollBehaviorX: 'contain',
    WebkitOverflowScrolling: 'touch',
    padding: '2px 2px 10px',
    ...theme.scrollbarStyles,
    [theme.breakpoints.down('xs')]: {
      scrollSnapType: 'x mandatory',
      gap: 10,
      padding: '2px 0 10px',
    },
    '@media (prefers-reduced-motion: reduce)': {
      scrollBehavior: 'auto',
    },
  },
}));

const normalize = value =>
  (value == null ? '' : String(value))
    .toLowerCase()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .trim();

/** Filtro client-side: nome do contato, telefone (só dígitos) ou nº do ticket. */
const ticketMatches = (ticket, q, qDigits) => {
  if (!q) return true;
  const name = normalize(ticket.contact && ticket.contact.name);
  if (name.includes(q)) return true;
  if (qDigits) {
    const number = String((ticket.contact && ticket.contact.number) || '').replace(/\D/g, '');
    if (number.includes(qDigits)) return true;
    if (String(ticket.id) === qDigits) return true;
  }
  return false;
};

const KanbanBoard = ({ lanes, query = '', onCardMove, onLaneReorder, updateTicket, isAdmin }) => {
  const classes = useStyles();

  const visibleLanes = useMemo(() => {
    const q = normalize(query);
    const qDigits = q.replace(/\D/g, '');
    if (!q) return lanes.map(lane => ({ ...lane, visibleTickets: lane.tickets }));
    return lanes.map(lane => ({
      ...lane,
      visibleTickets: lane.tickets.filter(ticket => ticketMatches(ticket, q, qDigits)),
    }));
  }, [lanes, query]);

  const handleDragEnd = result => {
    if (!result.destination) return;

    const { source, destination, draggableId, type } = result;

    // Se for reordenação de lane
    if (type === 'LANE') {
      if (source.index !== destination.index) {
        onLaneReorder(source.index, destination.index);
      }
      return;
    }

    // Se for movimentação de card
    if (
      source.droppableId !== destination.droppableId ||
      source.index !== destination.index
    ) {
      onCardMove(
        draggableId,
        destination.droppableId,
        source.droppableId,
        source.index,
        destination.index
      );
    }
  };

  return (
    <DragDropContext onDragEnd={handleDragEnd}>
      <Droppable droppableId="lanes" direction="horizontal" type="LANE">
        {provided => (
          <div
            className={classes.board}
            ref={provided.innerRef}
            {...provided.droppableProps}
          >
            {visibleLanes.map((lane, index) => (
              <KanbanColumn
                key={lane.id}
                id={lane.id}
                title={lane.title}
                tickets={lane.visibleTickets}
                totalCount={lane.tickets.length}
                filtered={Boolean(query)}
                color={lane.color}
                index={index}
                updateTicket={updateTicket}
                isAdmin={isAdmin}
              />
            ))}
            {provided.placeholder}
          </div>
        )}
      </Droppable>
    </DragDropContext>
  );
};

export default KanbanBoard;
